/**
 * EngineController — the single bridge between the document (Project), the
 * audio engine, the sequencer and MIDI. UI calls methods here and subscribes
 * to stores; it never touches audio nodes (AGENT.md §3, §6).
 */
import { AudioEngine } from "../audio/engine";
import {
  listAudioOutputs, playChannelTestTone, probeCapabilities, setOutputDevice, type AudioOutputInfo, type Capability,
} from "../audio/devices";
import { scheduleClick } from "../audio/instruments/click";
import { meterBallistics, peakToMeter } from "../audio/meter";
import { DEFAULT_DRUMKIT_PARAMS } from "../audio/instruments/drumkit";
import { DEFAULT_MULTISAMPLE_PARAMS, MultiSampleInstrument } from "../audio/instruments/multisample";
import { SamplerInstrument } from "../audio/instruments/sampler";
import { DEFAULT_SYNTH_PARAMS } from "../audio/instruments/synth";
import { setAudioSessionType } from "../audio/session";
import { AppError, errorMessage } from "../core/errors";
import { createId } from "../core/ids";
import { MidiInputRouter } from "../midi/router";
import { MidiService, type MidiPortInfo, type MidiStatus } from "../midi/service";
import { describeMidiEvent } from "../midi/parse";
import type { InstrumentConfig, Project } from "../project/schema";
import type { LocalStore } from "../project/storage";
import { transposeEvent, type SequencerEvent } from "../sequencer/events";
import { GM_DRUM_NOTE, clickEvents, countInEvents, drumEvents, progressionEvents, type DrumLane } from "../sequencer/patterns";
import { LookaheadScheduler, WorkerWakeTimer, type Dispatch, type LoopSource, type WakeTimer } from "../sequencer/scheduler";
import { PPQ, STEP_TICKS, barTicks } from "../sequencer/time";
import { History } from "./history";
import { SoundLibrary } from "./sounds";
import { Store } from "./store";
import { TrackGraph } from "./trackGraph";

export type { Capability };

export type AudioState = "not-started" | "starting" | "running" | "suspended" | "error";
export type SampleStatus = { status: "none" | "loading" | "ready" | "missing" | "error"; name?: string };
/** Loading state of a track's built-in sampled instrument. */
export type SoundStatus = { soundId: string; status: "loading" | "ready" | "error"; message?: string };
/** What a track can be switched to from the instrument picker. */
export type InstrumentChoice = { kind: "synth" } | { kind: "drumkit" } | { kind: "multisample"; soundId: string };
export interface Notice { id: number; level: "info" | "warning" | "error"; text: string; }

export interface LiveState {
  audio: AudioState;
  audioError: string | null;
  playing: boolean;
  /** Beats left in the count-in (null when not counting in). */
  countIn: number | null;
  heldNotes: number[];
  midi: MidiStatus;
  midiDevices: MidiPortInfo[];
  midiChannel: "omni" | number;
  midiLog: { id: number; text: string; port: string }[];
  samples: Record<string, SampleStatus>;
  sounds: Record<string, SoundStatus>;
  notices: Notice[];
  outputs: AudioOutputInfo[];
  outputDeviceId: string;
  droppedNotes: number;
}

/** Meter positions 0..1 (−60…0 dBFS), with peak-meter ballistics applied. */
export interface MeterState { tracks: Record<string, number>; master: number; }

export interface PreviewOptions { transpose?: boolean; }

export interface DocState { dirty: boolean; lastSavedAt: string | null; }

export interface ControllerDeps {
  createContext?: () => AudioContext;
  midi?: MidiService;
  storage?: LocalStore | null;
  timer?: WakeTimer;
  sounds?: SoundLibrary;
}

export const MAX_SAMPLE_BYTES = 50 * 1024 * 1024;
const MIDI_LOG_LIMIT = 200;
export const INFO_NOTICE_MS = 6000;
const DRUM_CHANNEL = 10; // GM convention

export class EngineController {
  readonly project: Store<Project>;
  readonly doc = new Store<DocState>({ dirty: false, lastSavedAt: null });
  readonly live: Store<LiveState>;
  /** Loop position in ticks, quantised to steps (null when stopped). */
  readonly position = new Store<number | null>(null);
  readonly history = new Store({ canUndo: false, canRedo: false });
  private undoStack = new History<Project>(100);
  /** Level meters; only computed while something subscribes (e.g. the mixer is visible). */
  readonly meters = new Store<MeterState>({ tracks: {}, master: 0 });

  storage: LocalStore | null;
  readonly midi: MidiService;

  private ctx: AudioContext | null = null;
  private engine: AudioEngine | null = null;
  private graph: TrackGraph | null = null;
  private scheduler: LookaheadScheduler | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private held = new Set<number>();
  private loopCache: { key: unknown[]; source: LoopSource } | null = null;
  private rafId: number | null = null;
  private meterRaf: number | null = null;
  private lastMeterAt = 0;
  private noticeSeq = 0;
  private logSeq = 0;
  private startPromise: Promise<boolean> | null = null;
  /** Set by the tuner while it holds the microphone, so startAudio() does not undo play-and-record. */
  micActive = false;
  private unsubs: (() => void)[] = [];

  private readonly keyboardTarget = {
    noteOn: (n: number, v: number) => this.playKeyboard(n, v),
    noteOff: (n: number) => this.releaseKeyboard(n),
    pitchBend: (semitones: number) => this.keyboardInstrument()?.pitchBend?.(semitones),
    modulation: (amount: number) => this.keyboardInstrument()?.modulation?.(amount),
  };

  private keyboardInstrument() {
    return this.graph?.get(this.project.get().playground.keyboardTrackId)?.instrument;
  }
  /** Separate routers so UI and hardware notes don't release each other. */
  readonly midiRouter = new MidiInputRouter(this.keyboardTarget);
  private uiRouter = new MidiInputRouter(this.keyboardTarget);

  private readonly soundLibrary: SoundLibrary;
  /** Session memory of a track's synth/drumkit settings while it uses a sampled instrument. */
  private previousInstruments = new Map<string, InstrumentConfig>();

  constructor(initial: Project, private deps: ControllerDeps = {}) {
    this.soundLibrary = deps.sounds ?? new SoundLibrary({ base: import.meta.env.BASE_URL });
    this.project = new Store(initial);
    this.storage = deps.storage ?? null;
    this.midi = deps.midi ?? new MidiService();
    this.live = new Store<LiveState>({
      audio: "not-started", audioError: null, playing: false, countIn: null, heldNotes: [], midi: this.midi.status, midiDevices: [],
      midiChannel: "omni", midiLog: [], samples: {}, sounds: {}, notices: [], outputs: [], outputDeviceId: "default", droppedNotes: 0,
    });
    this.syncLiveTranspose();
    this.unsubs.push(
      this.midi.statusChanged.subscribe((s) => this.patchLive({ midi: s })),
      this.midi.devicesChanged.subscribe((d) => this.patchLive({ midiDevices: d })),
      this.midi.inputDisconnected.subscribe(() => this.midiRouter.releaseAll()),
      this.midi.messages.subscribe((m) => {
        const e = m.event;
        const live = this.live.get();
        const log = [{ id: ++this.logSeq, text: describeMidiEvent(e), port: m.portName }, ...live.midiLog].slice(0, MIDI_LOG_LIMIT);
        this.patchLive({ midiLog: log });
        if (e.type === "noteon" && e.channel === DRUM_CHANNEL && live.midiChannel === "omni") {
          this.playDrumNote(e.note, e.velocity / 127);
          return;
        }
        void this.ensureAudioThen(() => this.midiRouter.handle(e));
      }),
    );
  }

  // ---------------------------------------------------------------- document

  /** Apply an immutable, undoable edit to the project. */
  updateProject(recipe: (p: Project) => Project): void {
    const prev = this.project.get();
    const next = recipe(prev);
    if (next === prev) return;
    this.undoStack.record(prev);
    this.commit(prev, next);
  }

  /** Group continuous edits (a slider drag) into one undo step. */
  beginEditGroup(): void { this.undoStack.beginGroup(); }
  endEditGroup(): void { this.undoStack.endGroup(); }

  undo(): void {
    const cur = this.project.get();
    const prev = this.undoStack.undo(cur);
    if (prev) this.commit(cur, this.keepIdentity(prev, cur));
  }

  redo(): void {
    const cur = this.project.get();
    const next = this.undoStack.redo(cur);
    if (next) this.commit(cur, this.keepIdentity(next, cur));
  }

  /** Undo/redo never rewinds identity or save bookkeeping (avoids false save conflicts). */
  private keepIdentity(snapshot: Project, cur: Project): Project {
    return { ...snapshot, id: cur.id, createdAt: cur.createdAt, updatedAt: cur.updatedAt, revision: cur.revision };
  }

  private commit(prev: Project, next: Project): void {
    this.project.set(next);
    this.doc.set({ ...this.doc.get(), dirty: true });
    this.syncHistory();
    this.applyToEngine(prev, next);
  }

  private syncHistory(): void {
    const { canUndo, canRedo } = this.undoStack;
    const h = this.history.get();
    if (h.canUndo !== canUndo || h.canRedo !== canRedo) this.history.set({ canUndo, canRedo });
  }

  /** Replace the whole document (open/new/import). */
  loadProject(p: Project, opts: { dirty?: boolean } = {}): void {
    const prev = this.project.get();
    this.stop();
    this.graph?.hardStop();
    this.project.set(p);
    this.undoStack.clear();
    this.syncHistory();
    this.doc.set({ dirty: opts.dirty ?? false, lastSavedAt: opts.dirty ? null : p.updatedAt });
    this.patchLive({ samples: {}, sounds: {}, droppedNotes: 0 });
    this.previousInstruments.clear();
    this.applyToEngine(prev, p);
    this.checkSampleAssets();
  }

  markSaved(p: Project): void {
    this.project.set(p);
    this.doc.set({ dirty: false, lastSavedAt: p.updatedAt });
  }

  private applyToEngine(prev: Project, next: Project): void {
    this.syncLiveTranspose();
    if (!this.engine || !this.graph || !this.scheduler) return;
    if (prev.master !== next.master) this.engine.setMasterVolume(next.master.volume);
    if (prev.effects.reverb !== next.effects.reverb) this.engine.reverb.setParams(next.effects.reverb);
    if (prev.effects.delay !== next.effects.delay) this.engine.delay.setParams(next.effects.delay);
    if (prev.metronome !== next.metronome) this.engine.setMonitorVolume(next.metronome.volume);
    if (prev.song.tempoBpm !== next.song.tempoBpm) this.scheduler.setTempo(next.song.tempoBpm);
    const assetChanges = this.graph.apply(next.tracks);
    for (const id of assetChanges) void this.loadTrackData(id);
  }

  private loadTrackData(trackId: string): Promise<void> {
    const kind = this.project.get().tracks.find((t) => t.id === trackId)?.instrument.kind;
    return kind === "multisample" ? this.loadSound(trackId) : this.loadSamplerAsset(trackId);
  }

  private syncLiveTranspose(): void {
    const s = this.project.get().song;
    const t = s.transposeLiveInput ? s.transposeSemitones : 0;
    this.midiRouter.setOptions({ transpose: t });
    this.uiRouter.setOptions({ transpose: t });
  }

  // ------------------------------------------------------------------- audio

  get audioContext(): AudioContext | null { return this.ctx; }
  /** For tests: the live track graph. */
  get trackGraph(): TrackGraph | null { return this.graph; }

  /** Create/resume the AudioContext. Must be called from a user gesture the first time. */
  startAudio(): Promise<boolean> {
    // iOS: loudspeaker routing, not the earpiece (the tuner switches to play-and-record while listening).
    if (!this.micActive) setAudioSessionType("playback");
    if (this.ctx && this.ctx.state === "running") return Promise.resolve(true);
    if (this.startPromise) return this.startPromise;
    this.startPromise = this.doStart().finally(() => { this.startPromise = null; });
    return this.startPromise;
  }

  private async doStart(): Promise<boolean> {
    try {
      if (!this.ctx) {
        this.patchLive({ audio: "starting", audioError: null });
        const create = this.deps.createContext ?? (() => {
          if (typeof AudioContext !== "function") throw new AppError("AUDIO_UNAVAILABLE", "This browser does not support the Web Audio API.");
          return new AudioContext({ latencyHint: "interactive" });
        });
        const ctx = create();
        this.ctx = ctx;
        const p = this.project.get();
        this.engine = new AudioEngine(ctx, { reverb: p.effects.reverb, delay: p.effects.delay, masterVolume: p.master.volume });
        this.engine.setMonitorVolume(p.metronome.volume);
        this.graph = new TrackGraph(this.engine);
        this.graph.apply(p.tracks);
        this.scheduler = new LookaheadScheduler(ctx, this.deps.timer ?? new WorkerWakeTimer(), () => this.loopSource());
        this.scheduler.setTempo(p.song.tempoBpm);
        this.scheduler.dispatched.subscribe((d) => this.onDispatch(d));
        ctx.onstatechange = () => this.patchLive({ audio: mapState(ctx.state) });
        this.checkSampleAssets();
        this.startMeters();
      }
      if (this.ctx.state === "suspended" && typeof this.ctx.resume === "function") await this.ctx.resume();
      this.patchLive({ audio: mapState(this.ctx.state) });
      return this.ctx.state === "running";
    } catch (err) {
      this.patchLive({ audio: "error", audioError: errorMessage(err) });
      this.notify("error", `Audio could not start: ${errorMessage(err)}`);
      return false;
    }
  }

  private async ensureAudioThen(fn: () => void): Promise<void> {
    if (this.ctx?.state === "running") { fn(); return; }
    if (await this.startAudio()) fn();
  }

  // ------------------------------------------------------------------- notes

  /** On-screen / computer-keyboard note input (velocity 0–1). */
  noteOn(note: number, velocity = 0.8): void {
    void this.ensureAudioThen(() => this.uiRouter.handle({ type: "noteon", channel: 1, note, velocity: Math.max(1, Math.round(velocity * 127)) }));
  }

  noteOff(note: number): void {
    this.uiRouter.handle({ type: "noteoff", channel: 1, note, velocity: 0 });
  }

  /** Release every on-screen/computer-keyboard note (window blur, focus loss) to avoid stuck notes. */
  noteOffAllUi(): void {
    this.uiRouter.releaseAll();
  }

  private playKeyboard(note: number, velocity: number): void {
    const t = this.graph?.get(this.project.get().playground.keyboardTrackId);
    if (!t) return;
    t.instrument.noteOn(note, velocity);
    this.held.add(note);
    this.patchLive({ heldNotes: [...this.held].sort((a, b) => a - b) });
  }

  private releaseKeyboard(note: number): void {
    this.graph?.get(this.project.get().playground.keyboardTrackId)?.instrument.noteOff(note);
    if (this.held.delete(note)) this.patchLive({ heldNotes: [...this.held].sort((a, b) => a - b) });
  }

  /** Audition notes (e.g. a chord from the theory panel) on the keyboard track, with song transposition. */
  previewNotes(notes: readonly number[], durationSec = 1, velocity = 0.7, opts: PreviewOptions = {}): void {
    this.previewSequence([notes], durationSec, { ...opts, velocity });
  }

  /**
   * Audition a sequence of note groups (scale steps or chords), one every `stepSec`,
   * scheduled on the audio clock. Song transposition applies unless `transpose: false`
   * (the Learning Lab plays exactly what the learner selected).
   */
  previewSequence(steps: readonly (readonly number[])[], stepSec: number, opts: PreviewOptions & { velocity?: number; gateRatio?: number } = {}): void {
    void this.ensureAudioThen(() => {
      const p = this.project.get();
      const inst = this.graph?.get(p.playground.keyboardTrackId)?.instrument;
      if (!inst || !this.ctx) return;
      const shift = opts.transpose === false ? 0 : p.song.transposeSemitones;
      const start = this.ctx.currentTime + 0.02;
      const gate = stepSec * (opts.gateRatio ?? 0.9);
      steps.forEach((group, i) => {
        const t = start + i * stepSec;
        for (const n of group) {
          const r = n + shift;
          if (r < 0 || r > 127) continue;
          inst.noteOn(r, opts.velocity ?? 0.7, t);
          inst.noteOff(r, t + gate);
        }
      });
    });
  }

  triggerDrum(lane: DrumLane, velocity = 0.9): void {
    void this.ensureAudioThen(() => this.playDrumNote(GM_DRUM_NOTE[lane], velocity));
  }

  private playDrumNote(note: number, velocity: number): void {
    const t = this.graph?.get(this.project.get().sequence.drums.trackId);
    t?.instrument.noteOn(note, velocity); // synthesised kit or sampled kit
  }

  triggerSample(velocity = 0.9): void {
    void this.ensureAudioThen(() => {
      const p = this.project.get();
      const t = this.graph?.get(p.playground.padsTrackId);
      if (t?.instrument instanceof SamplerInstrument) {
        if (!t.instrument.hasSample) { this.notify("info", "Load a sample first."); return; }
        const root = t.config.instrument.kind === "sampler" ? t.config.instrument.params.rootNote : 60;
        t.instrument.noteOn(root, velocity);
      }
    });
  }

  // --------------------------------------------------------------- transport

  async play(): Promise<void> {
    if (!(await this.startAudio()) || !this.scheduler || this.scheduler.isPlaying) return;
    const { song, metronome } = this.project.get();
    // Count-in clicks play on the monitor bus even when the metronome is off.
    this.scheduler.start(metronome.countInBars > 0 ? countInEvents(song.timeSignature, metronome.countInBars) : undefined);
    this.patchLive({ playing: true, droppedNotes: 0 });
    this.startPlayhead();
  }

  stop(): void {
    if (!this.scheduler?.isPlaying) return;
    this.scheduler.stop();
    // Release sequenced notes (keyboard notes on the same instrument are separate tracks).
    const p = this.project.get();
    this.graph?.get(p.sequence.progression.trackId)?.instrument.releaseAll();
    this.patchLive({ playing: false, countIn: null });
    this.stopPlayhead();
  }

  togglePlay(): void {
    if (this.scheduler?.isPlaying) this.stop();
    else void this.play();
  }

  /** Emergency stop: transport, every voice, MIDI outputs (AGENT.md §9). */
  panic(): void {
    this.stop();
    this.midiRouter.releaseAll();
    this.uiRouter.releaseAll();
    this.graph?.hardStop();
    this.held.clear();
    const sent = this.midi.panicOutputs();
    this.patchLive({ heldNotes: [] });
    this.notify("info", `Panic: all sound stopped${sent ? `, sent All Notes Off to MIDI outputs (${sent} messages)` : ""}.`);
  }

  private loopSource(): LoopSource {
    const p = this.project.get();
    const { sequence, song, metronome } = p;
    const key = [sequence, song.key, song.timeSignature, metronome.enabled];
    if (this.loopCache && key.every((k, i) => k === this.loopCache!.key[i])) return this.loopCache.source;
    const prog = sequence.progression;
    const bars = Math.max(1, prog.enabled ? prog.chords.length : 1);
    const events: SequencerEvent[] = [
      ...drumEvents(sequence.drums.pattern, sequence.drums.trackId, song.timeSignature, bars),
      ...(prog.enabled ? progressionEvents(prog, song.key, prog.trackId, song.timeSignature) : []),
      ...(metronome.enabled ? clickEvents(song.timeSignature, bars) : []),
    ];
    const source = { loopTicks: bars * barTicks(song.timeSignature), events };
    this.loopCache = { key, source };
    return source;
  }

  private onDispatch(d: Dispatch): void {
    if (!this.engine || !this.ctx) return;
    if (d.event.kind === "click") {
      scheduleClick(this.ctx, this.engine.monitorBus, d.timeSec, d.event.accent);
      return;
    }
    const { event, dropped } = transposeEvent(d.event, this.project.get().song.transposeSemitones);
    if (dropped) { this.patchLive({ droppedNotes: this.live.get().droppedNotes + 1 }); return; }
    if (!event || event.kind !== "note") return;
    const inst = this.graph?.get(event.trackId)?.instrument;
    if (!inst) return;
    inst.noteOn(event.note, event.velocity, d.timeSec);
    inst.noteOff(event.note, d.timeSec + d.durationSec);
  }

  /** Sample levels at most ~20×/s; skipped entirely when no UI is showing meters. */
  private startMeters(): void {
    if (typeof requestAnimationFrame !== "function") return;
    const loop = (now: number) => {
      this.meterRaf = requestAnimationFrame(loop);
      if (!this.engine || !this.meters.hasListeners || now - this.lastMeterAt < 50) return;
      const dt = this.lastMeterAt ? (now - this.lastMeterAt) / 1000 : 0;
      this.lastMeterAt = now;
      this.updateMeters(this.engine.readLevels(), dt);
    };
    this.meterRaf = requestAnimationFrame(loop);
  }

  /** Apply ballistics and publish only if something visibly changed. Public for tests. */
  updateMeters(levels: { tracks: Record<string, number>; master: number }, dtSec: number): void {
    const prev = this.meters.get();
    const tracks: Record<string, number> = {};
    let changed = false;
    // Skip imperceptible changes, but always publish reaching silence so meters never rest just above zero.
    const visible = (a: number, b: number) => Math.abs(a - b) > 0.005 || (a === 0 && b !== 0);
    for (const [id, peak] of Object.entries(levels.tracks)) {
      tracks[id] = meterBallistics(prev.tracks[id] ?? 0, peakToMeter(peak), dtSec);
      if (visible(tracks[id], prev.tracks[id] ?? 0)) changed = true;
    }
    const master = meterBallistics(prev.master, peakToMeter(levels.master), dtSec);
    if (changed || visible(master, prev.master) || Object.keys(tracks).length !== Object.keys(prev.tracks).length) {
      this.meters.set({ tracks, master });
    }
  }

  private startPlayhead(): void {
    if (typeof requestAnimationFrame !== "function") return;
    const loop = () => {
      const pos = this.scheduler?.loopPosition();
      const countIn = pos != null && pos < 0 ? Math.ceil(-pos / PPQ) : null;
      if (countIn !== this.live.get().countIn) this.patchLive({ countIn });
      this.position.set(pos == null || pos < 0 ? null : Math.floor(pos / STEP_TICKS) * STEP_TICKS);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stopPlayhead(): void {
    if (this.rafId !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.position.set(null);
  }

  // -------------------------------------------------------------------- MIDI

  async enableMidi(): Promise<void> {
    await this.midi.request();
  }

  setMidiChannel(channel: "omni" | number): void {
    this.midiRouter.releaseAll();
    this.midiRouter.setOptions({ channel });
    this.patchLive({ midiChannel: channel });
  }

  clearMidiLog(): void { this.patchLive({ midiLog: [] }); }

  // ----------------------------------------------------------------- samples

  /** Import a local audio file into the asset store and assign it to a sampler track. */
  async importSample(file: File, trackId = this.project.get().playground.padsTrackId): Promise<void> {
    try {
      if (file.size > MAX_SAMPLE_BYTES) throw new AppError("FILE_TOO_LARGE", `"${file.name}" is larger than 50 MB.`);
      if (file.type && !file.type.startsWith("audio/")) throw new AppError("FILE_INVALID", `"${file.name}" is not an audio file.`);
      if (!(await this.startAudio()) || !this.ctx) return;
      this.setSample(trackId, { status: "loading", name: file.name });
      const bytes = await file.arrayBuffer();
      let buffer: AudioBuffer;
      try { buffer = await this.ctx.decodeAudioData(bytes.slice(0)); } catch (e) {
        throw new AppError("AUDIO_DECODE_FAILED", `This browser could not decode "${file.name}". Try WAV or MP3.`, { cause: e });
      }
      const id = createId();
      this.buffers.set(id, buffer);
      await this.storage?.putAsset({
        id, name: file.name.slice(0, 200), type: "sample", mimeType: file.type || "application/octet-stream", sizeBytes: file.size,
        durationSec: buffer.duration, sampleRate: buffer.sampleRate, channels: buffer.numberOfChannels,
        createdAt: new Date().toISOString(), source: "user-import", licence: { kind: "user-provided" }, blob: file,
      });
      this.updateProject((p) => ({
        ...p,
        tracks: p.tracks.map((t) => (t.id === trackId && t.instrument.kind === "sampler" ? { ...t, instrument: { ...t.instrument, assetId: id } } : t)),
      }));
      if (!this.storage) this.notify("warning", "Local storage is unavailable: the sample will not survive a reload.");
    } catch (err) {
      this.setSample(trackId, { status: "error", name: file.name });
      this.notify("error", errorMessage(err));
    }
  }

  private async loadSamplerAsset(trackId: string): Promise<void> {
    const t = this.project.get().tracks.find((x) => x.id === trackId);
    const live = this.graph?.get(trackId);
    if (!t || t.instrument.kind !== "sampler" || !(live?.instrument instanceof SamplerInstrument) || !this.ctx) return;
    const assetId = t.instrument.assetId;
    if (!assetId) { live.instrument.setBuffer(null); this.setSample(trackId, { status: "none" }); return; }
    let buffer = this.buffers.get(assetId);
    let name = this.live.get().samples[trackId]?.name;
    if (!buffer) {
      const asset = await this.storage?.getAsset(assetId);
      if (!asset) { this.markMissing(trackId); return; }
      name = asset.name;
      this.setSample(trackId, { status: "loading", name });
      try {
        buffer = await this.ctx.decodeAudioData(await asset.blob.arrayBuffer());
        this.buffers.set(assetId, buffer);
      } catch {
        this.setSample(trackId, { status: "error", name });
        return;
      }
    } else if (!name) {
      name = (await this.storage?.getAsset(assetId))?.name;
    }
    live.instrument.setBuffer(buffer);
    this.setSample(trackId, { status: "ready", name });
  }

  /** Report missing assets even before audio starts (no decoding needed). */
  private checkSampleAssets(): void {
    for (const t of this.project.get().tracks) {
      if (t.instrument.kind === "multisample" && this.graph) void this.loadSound(t.id);
      if (t.instrument.kind !== "sampler" || !t.instrument.assetId) continue;
      if (this.graph) void this.loadSamplerAsset(t.id);
      else if (this.storage) {
        const id = t.instrument.assetId;
        void this.storage.getAsset(id).then((a) => (a ? this.setSample(t.id, { status: "ready", name: a.name }) : this.markMissing(t.id)));
      }
    }
  }

  // ------------------------------------------------------- built-in sounds

  /** Built-in sampled instruments (list + loading). */
  get sounds(): SoundLibrary { return this.soundLibrary; }

  /**
   * Switch a track's instrument from the picker. The track's previous settings
   * are remembered for this session, so switching back restores them; the
   * change itself is one undoable edit.
   */
  async setTrackInstrument(trackId: string, choice: InstrumentChoice): Promise<void> {
    const t = this.project.get().tracks.find((x) => x.id === trackId);
    if (!t) return;
    let next: InstrumentConfig;
    if (choice.kind === "multisample") {
      let releaseSec: number;
      try {
        const info = await this.soundLibrary.info(choice.soundId);
        if (!info) throw new AppError("SOUND_UNKNOWN", `Unknown instrument "${choice.soundId}".`);
        releaseSec = info.releaseSec;
      } catch (err) { this.notify("error", errorMessage(err)); return; }
      const prev = t.instrument.kind === "multisample" ? t.instrument.params : null;
      next = { kind: "multisample", soundId: choice.soundId, params: { ...DEFAULT_MULTISAMPLE_PARAMS, releaseSec, volume: prev?.volume ?? DEFAULT_MULTISAMPLE_PARAMS.volume } };
    } else {
      const remembered = this.previousInstruments.get(`${trackId}:${choice.kind}`);
      next = remembered ?? (choice.kind === "synth" ? { kind: "synth", params: { ...DEFAULT_SYNTH_PARAMS } } : { kind: "drumkit", params: { ...DEFAULT_DRUMKIT_PARAMS } });
    }
    const cur = this.project.get().tracks.find((x) => x.id === trackId);
    if (!cur) return;
    if (cur.instrument.kind === "synth" || cur.instrument.kind === "drumkit") this.previousInstruments.set(`${trackId}:${cur.instrument.kind}`, cur.instrument);
    this.updateProject((p) => ({ ...p, tracks: p.tracks.map((x) => (x.id === trackId ? { ...x, instrument: next } : x)) }));
  }

  /** Try loading a track's sampled instrument again (after a network error). */
  async retrySound(trackId: string): Promise<void> {
    if (await this.startAudio()) await this.loadSound(trackId);
  }

  private async loadSound(trackId: string): Promise<void> {
    const t = this.project.get().tracks.find((x) => x.id === trackId);
    const live = this.graph?.get(trackId);
    if (!t || t.instrument.kind !== "multisample" || !(live?.instrument instanceof MultiSampleInstrument) || !this.ctx) return;
    const { soundId } = t.instrument;
    const instrument = live.instrument;
    this.setSound(trackId, { soundId, status: "loading" });
    try {
      const set = await this.soundLibrary.load(this.ctx, soundId);
      // The track may have changed while downloading.
      const now = this.project.get().tracks.find((x) => x.id === trackId)?.instrument;
      if (now?.kind !== "multisample" || now.soundId !== soundId || this.graph?.get(trackId)?.instrument !== instrument) return;
      instrument.setSamples(set);
      this.setSound(trackId, { soundId, status: "ready" });
    } catch (err) {
      this.setSound(trackId, { soundId, status: "error", message: errorMessage(err) });
      this.notify("warning", `"${t.name}" is silent: ${errorMessage(err)}`);
    }
  }

  private setSound(trackId: string, s: SoundStatus): void {
    this.patchLive({ sounds: { ...this.live.get().sounds, [trackId]: s } });
  }

  private markMissing(trackId: string): void {
    this.setSample(trackId, { status: "missing" });
    const name = this.project.get().tracks.find((t) => t.id === trackId)?.name ?? "a track";
    this.notify("warning", `The sample used by "${name}" is missing from this browser's library. The track will stay silent until you load a sample.`);
  }

  private setSample(trackId: string, s: SampleStatus): void {
    this.patchLive({ samples: { ...this.live.get().samples, [trackId]: s } });
  }

  // ----------------------------------------------------------------- devices

  capabilities(): Capability[] {
    return probeCapabilities(this.ctx);
  }

  async refreshOutputs(): Promise<void> {
    try { this.patchLive({ outputs: await listAudioOutputs() }); } catch (err) { this.notify("warning", `Could not list outputs: ${errorMessage(err)}`); }
  }

  async setOutput(deviceId: string): Promise<void> {
    if (!(await this.startAudio()) || !this.ctx) return;
    try {
      await setOutputDevice(this.ctx, deviceId);
      this.patchLive({ outputDeviceId: deviceId });
    } catch (err) { this.notify("error", errorMessage(err)); }
  }

  async testChannel(channel: number): Promise<void> {
    if (!(await this.startAudio()) || !this.ctx) return;
    try { await playChannelTestTone(this.ctx, channel); } catch (err) { this.notify("error", errorMessage(err)); }
  }

  // ----------------------------------------------------------------- notices

  /** Info notices dismiss themselves; warnings and errors stay until dismissed. */
  notify(level: Notice["level"], text: string): void {
    const n = { id: ++this.noticeSeq, level, text };
    this.patchLive({ notices: [...this.live.get().notices, n].slice(-5) });
    if (level === "info" && typeof setTimeout === "function") setTimeout(() => this.dismissNotice(n.id), INFO_NOTICE_MS);
  }

  dismissNotice(id: number): void {
    this.patchLive({ notices: this.live.get().notices.filter((n) => n.id !== id) });
  }

  private patchLive(patch: Partial<LiveState>): void {
    this.live.set({ ...this.live.get(), ...patch });
  }

  async dispose(): Promise<void> {
    this.stopPlayhead();
    if (this.meterRaf !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(this.meterRaf);
    this.scheduler?.dispose();
    this.graph?.dispose();
    this.engine?.dispose();
    this.unsubs.forEach((u) => u());
    this.midi.dispose();
    if (this.ctx && "close" in this.ctx && this.ctx.state !== "closed") await this.ctx.close().catch(() => undefined);
    this.ctx = null;
  }
}

function mapState(s: AudioContextState | string): AudioState {
  if (s === "running") return "running";
  if (s === "suspended") return "suspended";
  return "error";
}
